import type Hls from 'hls.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatClock } from '../lib/format';
import { HLS_BUFFER_CONFIG } from '../lib/hls-config';
import type { LibraryMse } from '../lib/library-mse';
import { libraryNeedsMse } from '../lib/library-mse-math';
import { playbackSources, withLocalFallbacks } from '../lib/playback';
import { resumablePosition } from '../lib/progress';
import type { AccountSession } from '../lib/authContext';
import type { MovieDetail, WatchProgress } from '../lib/types';
import { playbackPreferencesStore, defaultSettings, settingsStore } from '../lib/storage';
import { measureMoviesThroughputKbps, peekMoviesThroughputKbps } from '../lib/moviesSpeedtest';
import { useI18n } from '../lib/use-i18n';
import { DistributionClient } from '../lib/api';

interface PlayerOverlayProps {
  movie: MovieDetail;
  session: AccountSession;
  resumeProgress?: WatchProgress;
  nextEpisodeLabel?: string;
  nextEpisodeReady?: boolean;
  autoPlayNext?: boolean;
  onPlayNext?: (automatic?: boolean) => void;
  onClose: () => void;
  onProgress: (progress: WatchProgress) => void;
  onClearProgress: (movieId: string) => void;
  onComplete: (movieId: string, duration: number) => void;
}

interface TrackOption { id: number; label: string; key: string; }
interface VideoWithAudioTracks extends HTMLVideoElement {
  audioTracks?: { length: number; [key: number]: { enabled: boolean; label: string; language: string } };
}

function selectNativeAudioTrack(video: VideoWithAudioTracks, selectedId: number): void {
  const tracks = video.audioTracks;
  if (!tracks) return;
  for (let index = 0; index < tracks.length; index += 1) tracks[index].enabled = index === selectedId;
}

function addPlaybackAuth(url: string, session: AccountSession): string {
  const parsed = new URL(url, window.location.origin);
  if (parsed.pathname.startsWith('/bcdn_token=')) return parsed.toString();
  parsed.searchParams.set('token', session.accessToken);
  parsed.searchParams.set('deviceId', session.deviceId);
  return parsed.toString();
}

export function PlayerOverlay({ movie, session, resumeProgress, nextEpisodeLabel, nextEpisodeReady = false, autoPlayNext = true, onPlayNext, onClose, onProgress, onClearProgress, onComplete }: PlayerOverlayProps) {
  const { t } = useI18n();
  const sources = useMemo(
    () => withLocalFallbacks(playbackSources(movie.playback)),
    [movie.playback],
  );
  const [sourceIndex, setSourceIndex] = useState(0);
  const source = sources[sourceIndex] ?? null;
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const mseRef = useRef<LibraryMse | null>(null);
  const mseFailed = useRef(false);
  const listedAudio = useRef(movie.audio);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(Boolean(source));
  const [error, setError] = useState(source ? '' : t('tryLater'));
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(movie.durationSec ?? 0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [audioTracks, setAudioTracks] = useState<TrackOption[]>([]);
  const [subtitles, setSubtitles] = useState<TrackOption[]>([]);
  const [audioId, setAudioId] = useState(0);
  const [subtitleId, setSubtitleId] = useState(-1);
  const [resumePrompt, setResumePrompt] = useState<number | null>(null);
  const [nextCountdown, setNextCountdown] = useState<number | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hlsRecoveryAttempts = useRef(0);
  const pendingAudioSwitch = useRef<number | null>(null);
  const initialProgress = useRef(resumeProgress);
  const playNextRef = useRef(onPlayNext);
  const resumeChoice = useRef<'undecided' | 'resume' | 'restart'>('undecided');
  const throughputKbpsRef = useRef(0);
  const fallbackPosition = useRef<number | null>(null);
  const playRequestedAt = useRef<number | null>(null);
  const firstFrameReported = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    const target = movie.firstFrameTelemetry;
    if (!video || !target || firstFrameReported.current || typeof video.requestVideoFrameCallback !== 'function') return;
    let callback = 0;
    const onFrame: VideoFrameRequestCallback = (_now, frame) => {
      if (firstFrameReported.current) return;
      // A decoded preview on a paused player is not playback startup.
      if (frame.presentedFrames < 1 || video.paused || video.ended || playRequestedAt.current === null) {
        callback = video.requestVideoFrameCallback(onFrame);
        return;
      }
      firstFrameReported.current = true;
      void new DistributionClient(settingsStore.read(), session)
        .reportFirstFrame(target, performance.now() - playRequestedAt.current).catch(() => {});
    };
    callback = video.requestVideoFrameCallback(onFrame);
    return () => video.cancelVideoFrameCallback(callback);
  }, [movie.firstFrameTelemetry, session, source]);

  useEffect(() => { playNextRef.current = onPlayNext; }, [onPlayNext]);
  useEffect(() => { listedAudio.current = movie.audio; }, [movie.audio]);

  const tryNextSource = useCallback((reason: string) => {
    console.warn(`[player] Source ${sourceIndex + 1} failed: ${reason}`);
    if (sourceIndex + 1 < sources.length) {
      const position = videoRef.current?.currentTime;
      if (position && Number.isFinite(position)) fallbackPosition.current = position;
      setError('');
      setBuffering(true);
      setResumePrompt(null);
      setSourceIndex(sourceIndex + 1);
      return;
    }
    setBuffering(false);
    setError(t('tryLater'));
  }, [sourceIndex, sources.length, t]);

  const saveProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.currentTime) || video.currentTime < 1) return;
    const progress: WatchProgress = {
      movieId: movie.id,
      currentTime: video.currentTime,
      duration: Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : (movie.durationSec ?? initialProgress.current?.duration ?? 0),
      updatedAt: Date.now(),
      ...(throughputKbpsRef.current > 0
        ? { throughputKbps: throughputKbpsRef.current }
        : {}),
    };
    onProgress(progress);
  }, [movie.durationSec, movie.id, onProgress]);

  // S4 netcheck download once per movie (tab cache) — not the movie media URL.
  useEffect(() => {
    if (!movie.id) return undefined;
    const cached = peekMoviesThroughputKbps(movie.id);
    if (cached > 0) {
      throughputKbpsRef.current = cached;
      return undefined;
    }
    const apiBase = settingsStore.read().apiBase || defaultSettings.apiBase;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void measureMoviesThroughputKbps({
        movieId: movie.id,
        apiBase,
      }).then((kbps) => {
        if (cancelled || !(kbps > 0)) return;
        throughputKbpsRef.current = kbps;
        saveProgress();
      });
    }, 2500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [movie.id, saveProgress]);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (!menuOpen && !videoRef.current?.paused) setControlsVisible(false);
    }, 3_500);
  }, [menuOpen]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !source) return;
    let cancelled = false;
    hlsRecoveryAttempts.current = 0;
    const onReady = () => {
      setBuffering(false);
      setDuration(Number.isFinite(video.duration) ? video.duration : (movie.durationSec ?? 0));
      const nativeVideo = video as VideoWithAudioTracks;
      if (nativeVideo.audioTracks) {
        const options = Array.from({ length: nativeVideo.audioTracks.length }, (_, id) => {
          const track = nativeVideo.audioTracks?.[id];
          const label = track?.label || track?.language || `Audio ${id + 1}`;
          return { id, label, key: (track?.language || label).toLowerCase() };
        });
        setAudioTracks(options);
        const preferred = playbackPreferencesStore.read().audio;
        const selected = options.find((option) => option.key === preferred);
        if (selected) { selectNativeAudioTrack(nativeVideo, selected.id); setAudioId(selected.id); }
      }
      const subtitleOptions = Array.from(video.textTracks).map((track, id) => {
        const label = track.label || track.language || `Subtitles ${id + 1}`;
        return { id, label, key: (track.language || label).toLowerCase() };
      });
      setSubtitles(subtitleOptions);
      const preferredSubtitle = playbackPreferencesStore.read().subtitles;
      const selectedSubtitle = subtitleOptions.find((option) => option.key === preferredSubtitle);
      if (selectedSubtitle) {
        Array.from(video.textTracks).forEach((track, index) => { track.mode = index === selectedSubtitle.id ? 'showing' : 'disabled'; });
        setSubtitleId(selectedSubtitle.id);
      }
      if (fallbackPosition.current !== null) {
        const position = fallbackPosition.current;
        fallbackPosition.current = null;
        video.currentTime = Number.isFinite(video.duration) ? Math.min(position, Math.max(0, video.duration - 0.1)) : position;
        setTime(video.currentTime);
        void video.play().catch(() => undefined);
        return;
      }
      const resumeAt = resumablePosition(initialProgress.current, video.duration);
      if (resumeChoice.current === 'undecided' && resumeAt !== null) {
        video.pause();
        setResumePrompt(resumeAt);
        return;
      }
      if (resumeChoice.current === 'resume' && resumeAt !== null) {
        video.currentTime = resumeAt;
        setTime(resumeAt);
      }
      void video.play().catch(() => undefined);
    };
    const onError = () => {
      if (!cancelled) tryNextSource(`media error ${video.error?.code ?? 'unknown'}`);
    };

    const loadNative = (url: string) => {
      video.src = addPlaybackAuth(url, session);
      video.addEventListener('loadedmetadata', onReady, { once: true });
      video.addEventListener('error', onError, { once: true });
      video.load();
    };

    const loadLibraryMse = async (url: string) => {
      const { LibraryMse: Player } = await import('../lib/library-mse');
      if (cancelled) return;
      video.addEventListener('loadedmetadata', onReady, { once: true });
      const mse = new Player(video, url, {
        chooseAudio: (tracks) => {
          const named = tracks.map((track) => {
            const listed = listedAudio.current.find((audio) => audio.language === track.language);
            const label = listed?.label || track.language || `Audio ${track.id + 1}`;
            return { id: track.id, label, key: (track.language || label).toLowerCase() };
          });
          setAudioTracks(named);
          const preferred = playbackPreferencesStore.read().audio;
          const defaultLanguage = listedAudio.current.find((audio) => audio.default)?.language;
          const chosen = named.find((option) => option.key === preferred)
            ?? named.find((option) => option.key === defaultLanguage?.toLowerCase())
            ?? named[0];
          setAudioId(chosen.id);
          return chosen.id;
        },
        onFatal: (reason) => {
          if (cancelled) return;
          console.warn(`[player] Audio switching is unavailable: ${reason}`);
          mseFailed.current = true;
          mse.destroy();
          mseRef.current = null;
          const position = video.currentTime;
          if (position && Number.isFinite(position)) fallbackPosition.current = position;
          setAudioTracks([]);
          video.removeEventListener('loadedmetadata', onReady);
          loadNative(url);
        },
      });
      mseRef.current = mse;
    };

    const initialize = async () => {
      if (source.kind === 'library' && movie.audio.length > 1 && !mseFailed.current && libraryNeedsMse(video)) {
        await loadLibraryMse(addPlaybackAuth(source.url, session));
        return;
      }
      if (source.kind !== 'hls') { loadNative(source.url); return; }
      const { default: HlsPlayer, Events, ErrorTypes } = await import('hls.js');
      if (cancelled) return;
      if (!HlsPlayer.isSupported()) { loadNative(source.url); return; }

      const hls = new HlsPlayer({
        enableWorker: true, capLevelToPlayerSize: true, capLevelOnFPSDrop: true,
        ...HLS_BUFFER_CONFIG,
        startFragPrefetch: true, autoStartLoad: false,
        xhrSetup: (xhr, url) => {
          // CDN segments use Bunny path tokens; account credentials stay on the API.
          if (new URL(url, window.location.href).origin !== new URL(source.url, window.location.href).origin) return;
          xhr.setRequestHeader('Authorization', `Bearer ${session.accessToken}`);
          xhr.setRequestHeader('X-Device-ID', session.deviceId);
        },
      });
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.loadSource(source.url);
      hls.on(Events.MANIFEST_PARSED, (_event, data) => {
        // Publications created before alternate-audio HLS support expose only a
        // muxed playlist. Prefer their progressive MP4 so playback still has a
        // browser-compatible AAC track instead of presenting an empty selector.
        if (movie.audio.length > 1 && data.audioTracks.length === 0) {
          hls.destroy();
          hlsRef.current = null;
          tryNextSource('alternate audio is unavailable in this playlist');
          return;
        }
        const audioOptions = data.audioTracks.map((track, id) => {
          const baseLabel = track.lang || track.name || `Audio ${id + 1}`;
          const duplicateCount = data.audioTracks.filter(
            (candidate) => (candidate.lang || candidate.name) === baseLabel,
          ).length;
          const label = duplicateCount > 1 ? `${baseLabel} · ${id + 1}` : baseLabel;
          const preferenceKey = duplicateCount > 1
            ? `${track.lang || baseLabel}:${id}`
            : (track.lang || label);
          return { id, label, key: preferenceKey.toLowerCase() };
        });
        const subtitleOptions = data.subtitleTracks.map((track, id) => {
          const label = track.name || track.lang || `Subtitles ${id + 1}`;
          return { id, label, key: (track.lang || label).toLowerCase() };
        });
        setAudioTracks(audioOptions);
        setSubtitles(subtitleOptions);
        const preferences = playbackPreferencesStore.read();
        const preferredAudio = audioOptions.find((option) => option.key === preferences.audio);
        if (preferredAudio) { hls.audioTrack = preferredAudio.id; setAudioId(preferredAudio.id); }
        const preferredSubtitle = subtitleOptions.find((option) => option.key === preferences.subtitles);
        if (preferredSubtitle) { hls.subtitleTrack = preferredSubtitle.id; setSubtitleId(preferredSubtitle.id); }
        // Select the preferred renditions before requesting media. Otherwise
        // HLS.js downloads the default audio first and restarts at playback.
        video.addEventListener('loadedmetadata', onReady, { once: true });
        hls.startLoad(-1);
      });
      hls.on(Events.AUDIO_TRACK_SWITCHING, () => setBuffering(true));
      hls.on(Events.AUDIO_TRACK_SWITCHED, (_event, data) => {
        setAudioId(data.id);
      });
      hls.on(Events.FRAG_BUFFERED, (_event, data) => {
        hlsRecoveryAttempts.current = 0;
        const expectedAudioTrack = pendingAudioSwitch.current;
        if (expectedAudioTrack !== null) {
          if (data.frag.type !== 'audio' || data.frag.level !== expectedAudioTrack) return;
          pendingAudioSwitch.current = null;
        }
        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) setBuffering(false);
      });
      hls.on(Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        if (data.type === ErrorTypes.NETWORK_ERROR && !source.url.includes('delivery=local')) {
          hls.destroy(); hlsRef.current = null; tryNextSource('network error; retry locally'); return;
        }
        if (hlsRecoveryAttempts.current < 2) {
          hlsRecoveryAttempts.current += 1;
          if (data.type === ErrorTypes.NETWORK_ERROR) {
            hls.startLoad(video.currentTime);
            return;
          }
          if (data.type === ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
        }
        hls.destroy();
        hlsRef.current = null;
        tryNextSource(`${data.type === ErrorTypes.NETWORK_ERROR ? 'network' : 'media'} error`);
      });
    };
    void initialize().catch(() => tryNextSource('player initialization error'));

    return () => {
      cancelled = true;
      saveProgress();
      hlsRef.current?.destroy();
      hlsRef.current = null;
      mseRef.current?.destroy();
      mseRef.current = null;
      pendingAudioSwitch.current = null;
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('error', onError);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (seekTimer.current) clearTimeout(seekTimer.current);
    };
  }, [movie.audio.length, movie.durationSec, movie.id, saveProgress, session, source, tryNextSource]);

  useEffect(() => {
    const persist = () => saveProgress();
    const persistWhenHidden = () => {
      if (document.visibilityState === 'hidden') persist();
    };
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      if (video && !video.paused && !video.ended) persist();
    }, 10_000);
    window.addEventListener('pagehide', persist);
    document.addEventListener('visibilitychange', persistWhenHidden);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pagehide', persist);
      document.removeEventListener('visibilitychange', persistWhenHidden);
    };
  }, [saveProgress]);

  useEffect(() => {
    if (nextCountdown === null || nextCountdown <= 0) return;
    const timer = window.setTimeout(() => {
      if (nextCountdown <= 1) playNextRef.current?.(true);
      else setNextCountdown(nextCountdown - 1);
    }, 1_000);
    return () => window.clearTimeout(timer);
  }, [nextCountdown]);

  useEffect(() => {
    const onBack = (event: Event) => {
      event.preventDefault();
      if (menuOpen) setMenuOpen(false);
      else onClose();
    };
    window.addEventListener('cyber-movies:back', onBack);
    return () => window.removeEventListener('cyber-movies:back', onBack);
  }, [menuOpen, onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const video = videoRef.current;
      if (!video) return;
      if (event.code === 'MediaPlayPause' || (event.code === 'Space' && event.target === document.body)) {
        event.preventDefault();
        void (video.paused ? video.play() : video.pause());
      }
      if (event.code === 'MediaRewind' || event.code === 'KeyJ') video.currentTime = Math.max(0, video.currentTime - 10);
      if (event.code === 'MediaFastForward' || event.code === 'KeyL') video.currentTime = Math.min(video.duration || Infinity, video.currentTime + 10);
      showControls();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showControls]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    void (video.paused ? video.play() : video.pause());
  };

  const seekTo = (target: number) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Math.max(0, Math.min(Number.isFinite(video.duration) ? video.duration : target, target));
    setTime(nextTime);
    setBuffering(true);
    if (seekTimer.current) clearTimeout(seekTimer.current);
    seekTimer.current = setTimeout(() => {
      video.currentTime = nextTime;
    }, 120);
  };

  const continuePlayback = () => {
    const video = videoRef.current;
    if (!video || resumePrompt === null) return;
    resumeChoice.current = 'resume';
    video.currentTime = resumePrompt;
    setTime(resumePrompt);
    setResumePrompt(null);
    void video.play().catch(() => undefined);
  };

  const startOver = () => {
    const video = videoRef.current;
    if (!video) return;
    resumeChoice.current = 'restart';
    video.currentTime = 0;
    setTime(0);
    setResumePrompt(null);
    onClearProgress(movie.id);
    void video.play().catch(() => undefined);
  };

  const selectAudio = (id: number) => {
    if (id === audioId) return;
    if (hlsRef.current) {
      setBuffering(true);
      pendingAudioSwitch.current = id;
      hlsRef.current.audioTrack = id;
    }
    else if (mseRef.current) {
      setBuffering(true);
      mseRef.current.selectAudio(id);
      setAudioId(id);
    }
    else {
      const video = videoRef.current as VideoWithAudioTracks | null;
      if (video) {
        selectNativeAudioTrack(video, id);
        setAudioId(id);
      }
    }
    const option = audioTracks.find((track) => track.id === id);
    const preferences = playbackPreferencesStore.read();
    playbackPreferencesStore.write({ ...preferences, audio: option?.key });
  };

  const selectSubtitle = (id: number) => {
    if (hlsRef.current) hlsRef.current.subtitleTrack = id;
    else if (videoRef.current) {
      Array.from(videoRef.current.textTracks).forEach((track, index) => { track.mode = index === id ? 'showing' : 'disabled'; });
    }
    setSubtitleId(id);
    const option = subtitles.find((track) => track.id === id);
    const preferences = playbackPreferencesStore.read();
    playbackPreferencesStore.write({ ...preferences, subtitles: option?.key });
  };

  return createPortal(
    <div className={`player-overlay ${controlsVisible ? 'controls-visible' : ''}`} ref={containerRef} role="dialog" aria-modal="true" aria-label={`Playing ${movie.title}`} onMouseMove={showControls} onClick={showControls}>
      <video
        ref={videoRef}
        className="player-video"
        playsInline
        crossOrigin="anonymous"
        onPlay={() => { playRequestedAt.current ??= performance.now(); setPlaying(true); }}
        onPause={() => { setPlaying(false); setControlsVisible(true); saveProgress(); }}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onSeeking={() => setBuffering(true)}
        onSeeked={() => setBuffering(false)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
        onEnded={(event) => {
          onComplete(movie.id, event.currentTarget.duration || duration);
          if (onPlayNext) setNextCountdown(nextEpisodeReady && autoPlayNext ? 8 : 0);
        }}
        onVolumeChange={(event) => { setVolume(event.currentTarget.volume); setMuted(event.currentTarget.muted); }}
      />
      <div className="player-shade" />
      {buffering && <div className="player-loader"><i />{t('preparingStream')}</div>}
      {error && <div className="player-error"><b>{t('titleUnavailable')}</b><span>{error}</span><button data-focusable="true" onClick={onClose}>{t('goBack')}</button></div>}
      {resumePrompt !== null && !error && (
        <section className="resume-dialog glass-panel" role="dialog" aria-modal="true" aria-label={t('continueWatching')}>
          <b>{t('continueQuestion')}</b>
          <span>{t('savedAt', { time: formatClock(resumePrompt) })}</span>
          <div>
            <button className="primary-action" data-focusable="true" data-autofocus="true" type="button" onClick={continuePlayback}>▶ {t('resume')}</button>
            <button className="secondary-action" data-focusable="true" type="button" onClick={startOver}>{t('startOver')}</button>
          </div>
        </section>
      )}
      {nextCountdown !== null && onPlayNext && !error && (
        <section className="next-episode-card glass-panel" role="dialog" aria-label={t('nextEpisode')}>
          <span>{t('nextEpisode')}</span>
          <b>{nextEpisodeLabel}</b>
          <small>{nextEpisodeReady ? (nextCountdown > 0 ? t('startingIn', { count: nextCountdown }) : t('readyToPlay')) : t('availableToAdd')}</small>
          <div>
            <button className="primary-action" data-focusable="true" data-autofocus="true" type="button" onClick={() => onPlayNext(false)}>{nextEpisodeReady ? `▶ ${t('playNow')}` : `＋ ${t('addEpisode')}`}</button>
            <button className="secondary-action" data-focusable="true" type="button" onClick={() => setNextCountdown(null)}>{t('cancel')}</button>
          </div>
        </section>
      )}

      <header className="player-topbar">
        <button className="player-icon-button" data-focusable="true" onClick={onClose} aria-label="Close player">←</button>
        <div><b>{movie.title}</b>{movie.year && <span>{movie.year}</span>}</div>
        <span className="live-badge">CYBER STREAM</span>
      </header>

      <div className="player-controls">
        <input
          className="player-timeline"
          aria-label="Playback position"
          type="range"
          min="0"
          max={duration || 1}
          value={Math.min(time, duration || 1)}
          onChange={(event) => seekTo(Number(event.target.value))}
          style={{ '--played': `${duration ? (time / duration) * 100 : 0}%` } as React.CSSProperties}
        />
        <div className="player-time"><span>{formatClock(time)}</span><span>−{formatClock(Math.max(0, duration - time))}</span></div>
        <div className="player-control-row">
          <div className="player-control-group">
            <button data-focusable="true" onClick={() => seekTo((videoRef.current?.currentTime ?? 0) - 10)} aria-label="Back 10 seconds">↶<small>10</small></button>
            <button className="play-toggle" data-focusable="true" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>{playing ? 'Ⅱ' : '▶'}</button>
            <button data-focusable="true" onClick={() => seekTo((videoRef.current?.currentTime ?? 0) + 10)} aria-label="Forward 10 seconds">↷<small>10</small></button>
          </div>
          <div className="player-control-group">
            {onPlayNext && <button data-focusable="true" type="button" onClick={() => onPlayNext(false)} aria-label={`Next episode${nextEpisodeLabel ? `: ${nextEpisodeLabel}` : ''}`}>▶｜</button>}
            <button data-focusable="true" onClick={() => { if (videoRef.current) videoRef.current.muted = !muted; }} aria-label="Sound">{muted || volume === 0 ? '⊘' : '◖'}</button>
            <input aria-label="Volume" type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={(event) => { if (videoRef.current) videoRef.current.volume = Number(event.target.value); }} />
            <button data-focusable="true" type="button" onClick={() => setMenuOpen((value) => !value)} aria-label="Playback settings" aria-expanded={menuOpen} aria-controls="player-settings-menu">⚙</button>
            <button data-focusable="true" onClick={() => void containerRef.current?.requestFullscreen()} aria-label="Fullscreen">⛶</button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <aside id="player-settings-menu" className="player-menu glass-panel">
          <div className="player-menu-head"><div><b>{t('audioSubtitles')}</b></div><button data-focusable="true" onClick={() => setMenuOpen(false)}>×</button></div>
          <PlayerMenuSection title={t('audio')} options={audioTracks} selected={audioId} onSelect={selectAudio} empty={t('noTracks')} />
          <PlayerMenuSection title={t('subtitles')} options={subtitles.length ? [{ id: -1, label: t('off'), key: '' }, ...subtitles] : []} selected={subtitleId} onSelect={selectSubtitle} empty={t('noSubtitles')} />
        </aside>
      )}
    </div>,
    document.body,
  );
}

function PlayerMenuSection({ title, options, selected, onSelect, empty }: {
  title: string;
  options: TrackOption[];
  selected: number;
  onSelect: (id: number) => void;
  empty: string;
}) {
  const safeOptions = options.length ? options : [{ id: -1, label: empty, key: '' }];
  return (
    <section className="player-menu-section">
      <h3>{title}</h3>
      <div>{safeOptions.map((option) => (
        <button data-focusable="true" disabled={!options.length} className={selected === option.id ? 'selected' : ''} key={`${title}-${option.id}`} onClick={() => onSelect(option.id)}>
          <span>{option.label}</span><i>{selected === option.id ? '●' : ''}</i>
        </button>
      ))}</div>
    </section>
  );
}
